import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import MaterialIcon from '@/components/ui/MaterialIcon';
import AppShell from '@/components/layout/AppShell';
import cardService from '@/services/cardService';
import benefitService from '@/services/benefitService';
import toast from 'react-hot-toast';

const DigitalCardPage: React.FC = () => {
  const { user } = useAuthStore();
  // const queryClient = useQueryClient(); // Removed unused variable


  const navigate = useNavigate();

  const { data: card, isLoading, error } = useQuery({
    queryKey: ['digitalCard', user?.id],
    queryFn: () => cardService.getMyCard(),
    enabled: !!user?.id,
  });

  const { data: benefitsData } = useQuery({
    queryKey: ['benefits'],
    queryFn: () => benefitService.getUserBenefits(),
    enabled: !!user?.id,
  });

  const coveredItems = React.useMemo(() => {
    if (!benefitsData) return [];
    const seen = new Set<string>();
    const items: string[] = [];
    for (const b of benefitsData.benefits) {
      if (b.coveragePercentage > 0 && !seen.has(b.category)) {
        seen.add(b.category);
        items.push(b.name);
      }
    }
    return items;
  }, [benefitsData]);

  // Save to offline storage when data is loaded
  React.useEffect(() => {
    if (card) {
      localStorage.setItem('leeris_offline_card', JSON.stringify({
        userName: `${user?.firstName} ${user?.lastName}`,
        memberNumber: user?.memberNumber || 'NUM-000000',
        dateOfBirth: user?.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString('fr-FR') : 'N/A',
        expiresAt: new Date(card.card.validUntil).toLocaleDateString('fr-FR'),
        qrCodeData: card.qrCodeUrl,
        lastUpdated: new Date().toISOString()
      }));
    }
  }, [card, user]);

  // We remove regenerateMutation as per feedback
  /*
  const regenerateMutation = useMutation({
    mutationFn: () => cardService.regenerateCard(),
    ...
  });
  */

  const downloadMutation = useMutation({
    mutationFn: () => cardService.downloadCardPDF(),
    onSuccess: (blob) => {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `carte_tiers_payant_${user?.firstName}_${user?.lastName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Téléchargement lancé');
    },
    onError: () => {
      toast.error('Erreur lors du téléchargement');
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background-light dark:bg-background-dark">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (error || !card) {
      return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4 bg-background-light dark:bg-background-dark p-4">
        <p className="text-gray-500">Impossible de charger la carte</p>
        <button
            onClick={() => window.location.reload()}
             className="rounded-xl bg-primary px-4 py-2 font-bold text-[#102216]"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <AppShell title="Ma Carte Tiers Payant" showBack backLabel="Accueil" onBack={() => navigate('/dashboard')}>
      <div className="flex flex-col gap-6 p-4 md:p-0 md:pt-4 md:max-w-xl">
        {/* Card Visualization */}
        <div className="relative w-full rounded-2xl overflow-hidden shadow-lg bg-gradient-to-br from-primary to-green-600 text-white p-6 aspect-[1.58/1]">
             {/* Background Pattern */}
             <div className="absolute top-0 right-0 -mr-8 -mt-8 h-32 w-32 rounded-full bg-white/20 blur-2xl" />
             <div className="absolute bottom-0 left-0 -ml-8 -mb-8 h-32 w-32 rounded-full bg-white/20 blur-2xl" />
             
             <div className="relative z-10 flex flex-col h-full justify-between">
                 <div className="flex justify-between items-start">
                     <img src="/logo-leeris.png" alt="Leeris" className="h-7 w-auto rounded-md bg-white px-2 py-1" />
                     <div className="bg-white/20 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium">Tiers Payant</div>
                 </div>

                  <div className="flex flex-col gap-1">
                      <p className="text-xs opacity-80 uppercase tracking-wider">Adhérent</p>
                      <p className="font-bold text-lg truncate">{user?.firstName} {user?.lastName}</p>
                      <div className="flex justify-between items-center text-sm opacity-90">
                        <span>{user?.memberNumber || 'NUM-000000'}</span>
                        <span className="text-[10px]">{user?.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString('fr-FR') : ''}</span>
                      </div>
                  </div>

                  <div className="flex justify-between items-end">
                      <div>
                          <p className="text-[10px] opacity-80 uppercase">Expire le</p>
                          <p className="text-sm font-medium">{new Date(card.card.validUntil).toLocaleDateString('fr-FR')}</p>
                      </div>
                  </div>
              </div>
        </div>

        {/* Coverage Details */}
        <div className="bg-surface-light dark:bg-surface-dark rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-white/5">
            <h3 className="text-sm font-bold text-[#111813] dark:text-white mb-3 flex items-center gap-2">
                <MaterialIcon icon="check_circle" className="text-primary" style={{ fontSize: 18 }} />
                Prestations couvertes par le Tiers Payant
            </h3>
            <div className="grid grid-cols-2 gap-y-2 gap-x-4">
                {coveredItems.length === 0 ? (
                    <p className="col-span-2 text-xs text-gray-500 dark:text-gray-400">
                        Aucune couverture disponible.
                    </p>
                ) : (
                    coveredItems.map((item) => (
                        <div key={item} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                            <div className="size-1.5 rounded-full bg-primary" />
                            {item}
                        </div>
                    ))
                )}
            </div>
        </div>

        {/* QR Code Section */}
        <div className="flex flex-col items-center gap-4 bg-white dark:bg-surface-dark p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-white/5">
            <h2 className="font-bold text-[#111813] dark:text-white">Présentez ce QR Code</h2>
            <div className="bg-white p-2 rounded-xl border border-gray-100">
                 {/* `qrCodeData` is a data URL string from backend */}
                <img
                  src={card.qrCodeUrl}
                  alt="QR Code de votre carte de tiers payant — à présenter au prestataire"
                  className="w-48 h-48 object-contain"
                />
            </div>
            <p className="text-center text-sm text-gray-500">
                Ce code permet aux prestataires de vérifier vos droits en temps réel.
            </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-4">
            <button 
                onClick={() => downloadMutation.mutate()}
                disabled={downloadMutation.isPending}
                className="flex items-center justify-center gap-2 bg-primary hover:bg-[#25d360] text-[#102216] font-bold py-4 rounded-xl shadow-md transition-all active:scale-[0.98] disabled:opacity-50"
            >
                 <MaterialIcon icon="download" />
                 <span>Télécharger ma carte (PDF)</span>
            </button>
            <p className="text-[10px] text-center text-gray-400 italic">
                En cas de changement de couverture, une nouvelle carte sera automatiquement mise à votre disposition.
            </p>
        </div>
      </div>
    </AppShell>
  );
};

export default DigitalCardPage;
